import React from "react";

import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Empty, Table } from "antd";
import Highlighter from "react-highlight-words";

import Loading from "../../Common/Loading";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";

import {
  mapRequestStatus, mapRequestType, isApprovedStatus, isRejectedStatus, parseNextPage,
} from "./helpers";

function renderStatus(status) {
  const label = mapRequestStatus(status);
  if (isRejectedStatus(label) || isRejectedStatus(status)) {
    return (
      <span className="TestDirectory__Status TestDirectory__Status--rejected">
        <CloseCircleOutlined aria-hidden />
        {label}
      </span>
    );
  }
  if (isApprovedStatus(label) || isApprovedStatus(status)) {
    return (
      <span className="TestDirectory__Status TestDirectory__Status--approved">
        <CheckCircleOutlined aria-hidden />
        {label}
      </span>
    );
  }
  return <span className="TestDirectory__Status">{label}</span>;
}

function renderHighlightedText(value, searchWords, fallback = "") {
  const text = value == null || value === "" ? fallback : String(value);
  if (!text) return fallback || "—";
  if (!searchWords.length) return text;
  return (
    <Highlighter
      highlightClassName="searchHighlight"
      searchWords={searchWords}
      autoEscape
      textToHighlight={text}
    />
  );
}

export default function TestRequests({
  requests = [],
  search = "",
  showResolution = false,
  paginationInfo = {},
  loading = false,
  loadingNewContent = false,
  onLoadMore,
  scrollbarRef,
}) {
  const searchWords = search.trim() ? [search.trim()] : [];

  const columns = showResolution
    ? [
      {
        title: "Test Name",
        dataIndex: "test_name",
        key: "test_name",
        render: (name) => renderHighlightedText(name, searchWords, "Unnamed test"),
      },
      {
        title: "Requested on",
        dataIndex: "request_date",
        key: "request_date",
        render: (date) => date || "—",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: renderStatus,
      },
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        render: (reason) => renderHighlightedText(reason, searchWords, "No reason provided"),
      },
    ]
    : [
      {
        title: "Test Name",
        dataIndex: "test_name",
        key: "test_name",
        render: (name) => renderHighlightedText(name, searchWords, "Unnamed test"),
      },
      {
        title: "Requested on",
        dataIndex: "request_date",
        key: "request_date",
        render: (date) => date || "—",
      },
      {
        title: "Request type",
        key: "request_type",
        render: (_, record) => mapRequestType(record.type || record.request_type),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: renderStatus,
      },
    ];

  const dataSource = requests.map((request, index) => ({
    ...request,
    key: request.id || `${request.test_name || "request"}-${request.request_date || index}`,
  }));

  const handleScrollToBottom = (e) => {
    const tolerance = 3;
    const bottom = Math.abs(
      Math.ceil(e.contentScrollHeight - e.scrollTop) - Math.ceil(e.clientHeight),
    ) <= tolerance;
    if (!bottom) return;

    const {
      nextPageLink, currentPage, totalPages,
    } = paginationInfo;
    const isLastPage = parseInt(currentPage, 10) >= parseInt(totalPages, 10);

    if (!isLastPage && !loadingNewContent && !loading && onLoadMore) {
      const nextPage = nextPageLink
        ? (parseNextPage(nextPageLink, currentPage) || String(parseInt(currentPage, 10) + 1))
        : String(parseInt(currentPage, 10) + 1);
      onLoadMore(nextPage);
    }
  };

  return (
    <div className="TestDirectory__RequestTableWrap">
      <StyledScrollbar
        ref={scrollbarRef}
        className="TestDirectory__RequestTableScroll"
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
        onScrollStop={(e) => handleScrollToBottom(e)}
      >
        <Table
          className="TestDirectory__RequestTable"
          size="small"
          pagination={false}
          columns={columns}
          dataSource={dataSource}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={showResolution ? "No past requests" : "No pending requests"}
              />
            ),
          }}
        />
        {loadingNewContent && !loading && requests.length > 0 && <Loading />}
      </StyledScrollbar>
    </div>
  );
}
